import React, { Fragment, useState } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { createStructuredSelector } from 'reselect';
import PropTypes from 'prop-types';
import { Button, F } from 'components/Ui';
import { capitalize } from 'lodash';
import { accountIcons } from 'images';
import ClearIcon from '@material-ui/icons/Clear';
import * as actions from 'containers/PrimoProfile/actions';
import { selectIsProcessing } from 'containers/PrimoProfile/selectors';
import { showDrawer as showDrawerAction } from 'containers/DrawerManager/actions';
import {
  EDIT_BANK_ACCOUNT,
  EDIT_CREDIT_CARD,
} from 'containers/PrimoProfile/BillingInformation/constants';
import { isCardExpired } from 'containers/PrimoProfile/Payment/utils';
import { isCanadianBrand } from 'utils/translation';
import EditCreditCardDrawer from './CreditCardForm/EditCreditCardDrawer';
import EditBankAccountDrawer from './BankAccountForm/EditBankAccountDrawer';

import styles from './styles.scss';

const renderAccountInfo = (bank) => (
  <Fragment>
    <div className={styles.paymentContainer}>
      <div className={styles.paymentIconContainer}>
        <span className={styles.paymentIcon}>
          {' '}
          <img src={accountIcons.Bank} alt="" />
        </span>
      </div>
      <div>
        <div className={styles.bankText}>
          <span className={styles.number}>
            <F>{capitalize(bank.accountType)}</F> <F>ending in</F> {bank.last4}
          </span>
        </div>
        <div></div>
      </div>
    </div>
  </Fragment>
);

const renderCardInfo = (card) => {
  const isExpired = isCardExpired(card);
  return (
    <Fragment>
      <div className={styles.paymentContainer}>
        <div className={styles.paymentIconContainer}>
          <span className={styles.paymentIcon}>
            {' '}
            <img src={accountIcons.CreditCard} alt="" />
          </span>
        </div>
        <div>
          <div>
            <span className={styles.number}>
              <F>Credit/debit card ending in</F> {card.last4}
            </span>
          </div>
          <div>
            <span
              className={classnames(
                styles.date,
                styles.expireText,
                isExpired && styles.expired,
              )}
            >
              <F>Expires</F> {card.expirationMonth}/20{card.expirationYear}
            </span>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

const PaymentMethodBlock = ({
  type,
  locked: isLocked = false,
  data: method,
  onClose,
  deleteCreditCard,
  deleteBankAccount,
  updateAutopay,
}) => {
  const DrawerFormComponent =
    type === 'account' ? EditBankAccountDrawer : EditCreditCardDrawer;
  const key =
    type === 'account'
      ? `${EDIT_BANK_ACCOUNT}-${method.id}`
      : `${EDIT_CREDIT_CARD}-${method.id}`;
  const [showDeleteConfirmation, changeDeleteConfirmation] = useState(false);

  const handleDeletePaymentMethod = () => {
    if (type === 'account') {
      deleteBankAccount(method.id);
    } else if (type === 'card') {
      deleteCreditCard(method.id);
    }
  };

  const handleMakeAutopay = () => {
    updateAutopay(method);
  };

  const handleShowDeleteConfirmation = () => {
    changeDeleteConfirmation(true);
  };

  const handleCancelDeleteConfirmation = () => {
    changeDeleteConfirmation(false);
  };

  const renderPaymentMethod = () => (
    <Fragment>
      {type === 'account' ? renderAccountInfo(method) : renderCardInfo(method)}
    </Fragment>
  );

  const renderRemovePaymentText = () => (
    <Fragment>
      <span>Are you sure you want to remove this payment method?</span>
    </Fragment>
  );
  const isAutoPayAvailable = () => !(!isCanadianBrand() && type === 'account');

  return (
    <div
      className={classnames(styles.paymentMethodBlock, {
        [styles.focus]: showDeleteConfirmation,
        [styles.locked]: isLocked,
      })}
    >
      <div className={styles.info}>
        {!showDeleteConfirmation
          ? renderPaymentMethod()
          : renderRemovePaymentText()}
      </div>
      <div className={styles.buttons}>
        {!isLocked && !showDeleteConfirmation && isAutoPayAvailable() && (
          <Fragment>
            <Button
              link
              onClick={handleMakeAutopay}
              text="Make Autopay"
              type="button"
            />
          </Fragment>
        )}
        {!isLocked && !showDeleteConfirmation && (
          <Fragment>
            <Button
              className={styles.clearIcon}
              link
              icon={<ClearIcon />}
              onClick={handleShowDeleteConfirmation}
              type="button"
            />
          </Fragment>
        )}
        {!isLocked && showDeleteConfirmation && (
          <Fragment>
            <Button
              link
              onClick={handleDeletePaymentMethod}
              text="Remove"
              type="button"
            />
            <Button
              link
              text="Cancel"
              onClick={handleCancelDeleteConfirmation}
              type="button"
            />
          </Fragment>
        )}
      </div>
      <DrawerFormComponent
        drawerProps={{ onClose }}
        formProps={{ defaultValues: method }}
        drawerKey={key}
      />
    </div>
  );
};

PaymentMethodBlock.propTypes = {
  locked: PropTypes.bool,
  type: PropTypes.oneOf(['card', 'account']),
  data: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  deleteCreditCard: PropTypes.func.isRequired,
  deleteBankAccount: PropTypes.func.isRequired,
  updateAutopay: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  isProcessing: selectIsProcessing(),
});

const mapDispatchToProps = {
  deleteCreditCard: actions.deleteCreditCard,
  deleteBankAccount: actions.deleteBankAccount,
  updateAutopay: actions.updateAutoPay,
  showDrawer: showDrawerAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(PaymentMethodBlock);
